import { Component, OnDestroy, OnInit } from '@angular/core';
import { catchError, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { GeneralConfig } from '@core/models/header-config.model';
import { GeneralConfigService } from '@core/services/general-config.service';
import { environment } from '@environments/environment';
import { AuthService, IdleUserService } from '@capturum/auth';
import { MessageService } from 'primeng/api';
import { CapturumDialogService, LocalStorageService } from '@capturum/ui/api';
import { finalize, first } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes } from '@core/enums/routes.enum';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { StationStateService } from '@shared/services/station-state.service';
import { ApiHttpService } from '@capturum/api';
import { SidebarService } from '@shared/services/sidebar.service';

@Component({
  selector: 'app-basic-layout',
  templateUrl: './basic-layout.component.html',
  styleUrls: ['./basic-layout.component.scss'],
})
export class BasicLayoutComponent implements OnInit, OnDestroy {
  public generalConfig$: Observable<GeneralConfig>;
  public idleToastKey = 'idle-toast';
  public expiryKey = 'user.idle.expiry';
  public userRole: string;

  public timeoutDurationInMilliseconds = environment.timeoutDuration * 1000;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private idleExpiration: number;
  private loggedOutSubject: Subject<boolean> = new Subject<boolean>();
  private isLoggingOut = false;

  constructor(
    private readonly generalConfigService: GeneralConfigService,
    private readonly translateService: TranslateService,
    private router: Router,
    private authService: AuthService,
    private idleUserService: IdleUserService,
    private messageService: MessageService,
    private storageService: LocalStorageService,
    private dialogService: DialogService,
    private capturumDialogService: CapturumDialogService,
    private stationStateService: StationStateService,
    private apiHttpService: ApiHttpService,
    private sidebarService: SidebarService,
  ) {}

  public ngOnInit(): void {
    this.generalConfig$ = this.generalConfigService.getGeneralConfig();
    this.idleExpiration = this.storageService.getItem(this.expiryKey);
    this.userRole = JSON.parse(localStorage.getItem('user'))?.currentRole?.key;

    this.idleUserService.watchIdle();
    this.listenIdleTimeout();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public idleToastClose(): void {
    this.messageService.clear();
    this.idleUserService.resetTimer();
    this.listenIdleTimeout();
  }

  private listenIdleTimeout(): void {
    this.idleUserService
      .getIdleStart()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.idleExpiration = this.storageService.getItem(this.expiryKey);
      });

    this.idleUserService
      .getIdleTimeoutWarning()
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.messageService.add({
          key: this.idleToastKey,
          sticky: true,
          severity: 'warn',
          summary: this.translateService.instant('hpm.idle.toast.message.summary.label'),
          detail: this.translateService.instant('hpm.idle.toast.message.details.label'),
        });
      });

    this.idleUserService
      .getIdleTimeout()
      .pipe(
        first(),
        takeUntil(this.loggedOutSubject),
        switchMap(() => {
          return this.logout();
        }),
      )
      .subscribe();
  }

  private logout(): Observable<boolean> {
    if (this.isLoggingOut) {
      return of(false);
    }

    this.isLoggingOut = true;
    const stationId = this.stationStateService.getStation()?.id;

    this.messageService.clear();

    this.dialogService.dialogComponentRefMap.forEach((dialog) => {
      dialog.destroy();
    });
    this.capturumDialogService.dialogComponentRefMap.forEach((dialog) => {
      dialog.destroy();
    });
    this.sidebarService.close();
    this.idleUserService.stopWatching();

    return this.apiHttpService.patch('/automatic-logout/logic', { station_id: stationId }).pipe(
      switchMap(() => {
        return this.authService.logout();
      }),
      first(),
      finalize(() => {
        this.isLoggingOut = false;

        this.router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
      }),
      catchError(() => {
        this.isLoggingOut = false;

        return of(null);
      }),
    );
  }
}
